import {
  ComplexPromotionTypes,
  IDeferredPromotion,
  PromotionSelectionCriteria,
} from '../../graphQL/commons'
import {
  IFreeGood,
  IFreegoodsConf,
  IFreegoodsQuantitySelection,
  IFreegoodsQuantitySelectionGroup,
  IProduct,
} from '../../interfaces'
import { castIProduct } from '../castICatalogItems'

export const getFreegoodsFromDeferredPromotions = (
  deferredPromotions: IDeferredPromotion[] | null,
): IFreegoodsConf | undefined => {
  const deferredPromotion = deferredPromotions?.find(
    (currentPromotion) =>
      currentPromotion.complexConfiguration?.selectBy !==
      PromotionSelectionCriteria.REFUSED,
  )

  if (deferredPromotion) {
    const {
      promotion,
      count,
      complexConfiguration,
      promotion: {
        customerGets: { groups, itemsExtra },
      },
    } = deferredPromotion

    const hasQuantitySelectionGroup = groups.some(
      ({ type }) => type === ComplexPromotionTypes.QUANTITY_SELECTION,
    )

    const selectBy =
      complexConfiguration?.selectBy ||
      (hasQuantitySelectionGroup
        ? PromotionSelectionCriteria.QUANTITY_SELECTION
        : PromotionSelectionCriteria.CUSTOMER_SELECTION)

    const finalCount = count || 1
    const quantitySelectionGroups: IFreegoodsQuantitySelectionGroup[] = []
    const quantitySelectionProducts: IProduct[] = []

    const availableFreegoods: IFreeGood[] = []

    groups.forEach(
      ({ items, type, identifier, maxSkuSelectionQuantity }, index) => {
        const normalizedIdentifier = identifier || `freegood-${index}`

        if (type === ComplexPromotionTypes.QUANTITY_SELECTION) {
          const fallbackQuantity = items.reduce<number>(
            (acc, { quantity }) =>
              Math.max(acc, Number(quantity !== null ? quantity : 0)),
            0,
          )

          const maxSkuQuantityBase = Math.max(
            0,
            Number(maxSkuSelectionQuantity ?? fallbackQuantity),
          )

          const maxTotalQuantity = finalCount * maxSkuQuantityBase
          const products: IProduct[] = []

          items.forEach(({ sku }) => {
            if (!sku) {
              return
            }
            const productExtra = itemsExtra?.find(
              ({ sku: skuItemExtra }) => skuItemExtra === sku,
            )

            if (!productExtra) {
              return
            }

            const castedProduct = castIProduct(productExtra)
            products.push(castedProduct)
            quantitySelectionProducts.push(castedProduct)
          })

          if (products.length > 0) {
            quantitySelectionGroups.push({
              identifier: normalizedIdentifier,
              maxTotalQuantity,
              products,
            })
          }
          return
        }

        if (type !== ComplexPromotionTypes.QUANTITY) {
          return
        }

        const shouldAddDescription = items.length === 1
        const result = items.reduce<IFreeGood>(
          (freeGoodAcc, { sku, quantity: quantityExtra }) => {
            const product = itemsExtra?.find(
              ({ sku: skuItemExtra }) => skuItemExtra === sku,
            )
            if (product) {
              const { name, description, imageURL } = product
              const {
                namesDescriptions,
                imageUrl: currentImage,
                ...rest
              } = freeGoodAcc

              const totalQuantity = Number(quantityExtra) * finalCount
              return {
                ...rest,
                imageUrl: currentImage || imageURL?.[0],
                namesDescriptions: [
                  ...namesDescriptions,
                  {
                    name:
                      totalQuantity > 1 ? `(${totalQuantity}) ${name}` : name,
                    description: shouldAddDescription ? description : '',
                  },
                ],
              }
            }
            return freeGoodAcc
          },
          {
            id: normalizedIdentifier,
            isChecked: false,
            namesDescriptions: [],
          },
        )

        if (result.namesDescriptions.length > 0) {
          availableFreegoods.push(result)
        }
      },
    )

    const quantitySelection: IFreegoodsQuantitySelection | undefined =
      quantitySelectionGroups.length > 0
        ? {
            groups: quantitySelectionGroups,
            products: quantitySelectionProducts,
            maxTotalQuantity: quantitySelectionGroups.reduce(
              (maxQuantity, currentGroup) =>
                Math.max(maxQuantity, currentGroup.maxTotalQuantity),
              0,
            ),
            totalSelected: 0,
          }
        : undefined

    return {
      promotion,
      freegoods: availableFreegoods,
      isConfirmDisabled: true,
      selectBy,
      quantitySelection,
      maxQuantity: quantitySelection?.maxTotalQuantity,
    }
  }
}
